import type { CalendarEvent } from '../types/calendarEvent';

interface FacilityRef {
  id?: string | null;
  facilityName?: string | null;
}

interface BookingEntry {
  id: string;
  documentNumber?: string | null;
  cutOffDate: string | null;
  status?: string | null;
}

interface LoadEntry {
  id: string;
  documentNumber?: string | null;
  expectedShipDate: string | null;
  status?: string | null;
  facility?: FacilityRef | null;
}

interface SalesOrderEntry {
  id: string;
  documentNumber?: string | null;
  startDate: string | null;
  endDate?: string | null;
  status?: string | null;
  facility?: FacilityRef | null;
}

interface RouteEntry {
  id: string;
  documentNumber?: string | number | null;
  routeDate: string | null;
  status?: string | null;
}

interface DriverCertificationEntry {
  id: string;
  driver?: { firstName?: string | null; lastName?: string | null } | null;
  type?: string | null;
  number?: string | null;
  expirationDate: string | null;
  status?: string | null;
}

interface WorkOrderEntry {
  id: string;
  workOrderNumber?: string | null;
  scheduledStartDate: string | null;
  scheduledEndDate?: string | null;
  status?: string | null;
  facility?: FacilityRef | null;
}

interface TollingAgreementEntry {
  id: string;
  agreementNumber?: string | null;
  effectiveDate: string | null;
  expirationDate?: string | null;
  isActive?: boolean | null;
  facility?: FacilityRef | null;
}

interface DispatchRecordEntry {
  id: string;
  dispatchType?: string | null;
  externalReferenceId?: string | null;
  submittedAt: string | null;
  status?: string | null;
  facilityId?: string | null;
}

interface InvoiceEntry {
  id: string;
  documentNumber?: string | null;
  dueDate: string | null;
  status?: string | null;
  amount?: unknown;
  facility?: FacilityRef | null;
}

interface BillEntry {
  id: string;
  documentNumber?: string | null;
  dueDate: string | null;
  status?: string | null;
  amount?: unknown;
  facility?: FacilityRef | null;
}

interface InventoryMovementEntry {
  id: string;
  movementType?: string | null;
  occurredAt: string | null;
  status?: string | null;
  facilityId?: string | null;
  item?: { name?: string | null; skuNumber?: string | null } | null;
  quantity?: unknown;
}

export function normalizeBookings(data: BookingEntry[]): CalendarEvent[] {
  if (!data) return [];
  return data
    .filter((item) => item.cutOffDate != null)
    .map(
      (item): CalendarEvent => ({
        id: item.id,
        title: item.documentNumber
          ? `Booking ${item.documentNumber}`
          : `Booking ${item.id?.slice(0, 8)}`,
        start: item.cutOffDate ?? '',
        allDay: true,
        module: 'trade',
        source: 'booking',
        kind: 'point',
        entityUrl: `/trade/bookings/${item.id}`,
        facilityId: undefined,
        facilityName: undefined,
        status: item.status ?? undefined,
        meta: { documentNumber: item.documentNumber },
      }),
    );
}

export function normalizeLoads(data: LoadEntry[]): CalendarEvent[] {
  if (!data) return [];
  return data
    .filter((item) => item.expectedShipDate != null)
    .map(
      (item): CalendarEvent => ({
        id: item.id,
        title: item.documentNumber
          ? `Load ${item.documentNumber}`
          : `Load ${item.id?.slice(0, 8)}`,
        start: item.expectedShipDate ?? '',
        allDay: false,
        module: 'trade',
        source: 'load',
        kind: 'point',
        entityUrl: `/trade/loads/${item.id}`,
        facilityId: item.facility?.id ?? undefined,
        facilityName: item.facility?.facilityName ?? undefined,
        status: item.status ?? undefined,
        meta: { documentNumber: item.documentNumber },
      }),
    );
}

export function normalizeSalesOrders(data: SalesOrderEntry[]): CalendarEvent[] {
  if (!data) return [];
  return data
    .filter((item) => item.startDate != null)
    .map(
      (item): CalendarEvent => ({
        id: item.id,
        title: item.documentNumber
          ? `SO ${item.documentNumber}`
          : `Sales Order ${item.id?.slice(0, 8)}`,
        start: item.startDate ?? '',
        end: item.endDate ?? undefined,
        allDay: true,
        module: 'trade',
        source: 'salesOrder',
        kind: 'range',
        entityUrl: `/trade/orders/${item.id}`,
        facilityId: item.facility?.id ?? undefined,
        facilityName: item.facility?.facilityName ?? undefined,
        status: item.status ?? undefined,
        meta: { documentNumber: item.documentNumber },
      }),
    );
}

export function normalizeRoutes(data: RouteEntry[]): CalendarEvent[] {
  if (!data) return [];
  return data
    .filter((item) => item.routeDate != null)
    .map(
      (item): CalendarEvent => ({
        id: item.id,
        title: item.documentNumber
          ? `Route ${item.documentNumber}`
          : `Route ${item.id?.slice(0, 8)}`,
        start: item.routeDate ?? '',
        allDay: false,
        module: 'transportation',
        source: 'route',
        kind: 'point',
        entityUrl: `/transportation/routes/${item.id}`,
        facilityId: undefined,
        facilityName: undefined,
        status: item.status ?? undefined,
        meta: { documentNumber: item.documentNumber },
      }),
    );
}

export function normalizeDriverCertifications(
  data: DriverCertificationEntry[],
): CalendarEvent[] {
  if (!data) return [];
  return data
    .filter((item) => item.expirationDate != null)
    .map((item): CalendarEvent => {
      const driverName = [item.driver?.firstName, item.driver?.lastName]
        .filter(Boolean)
        .join(' ');
      return {
        id: item.id,
        title:
          [driverName, item.type].filter(Boolean).join(' - ') ||
          `Certification ${item.id?.slice(0, 8)}`,
        start: item.expirationDate ?? '',
        allDay: true,
        module: 'transportation',
        source: 'driverCertification',
        kind: 'point',
        entityUrl: `/transportation/driver-certifications/${item.id}`,
        facilityId: undefined,
        facilityName: undefined,
        status: item.status ?? undefined,
        meta: {
          driverName,
          certificationType: item.type,
          number: item.number,
        },
      };
    });
}

export function normalizeWorkOrders(data: WorkOrderEntry[]): CalendarEvent[] {
  if (!data) return [];
  return data
    .filter((item) => item.scheduledStartDate != null)
    .map(
      (item): CalendarEvent => ({
        id: item.id,
        title: item.workOrderNumber
          ? `WO ${item.workOrderNumber}`
          : `Work Order ${item.id?.slice(0, 8)}`,
        start: item.scheduledStartDate ?? '',
        end: item.scheduledEndDate ?? undefined,
        allDay: true,
        module: 'production',
        source: 'workOrder',
        kind: 'range',
        entityUrl: `/production/work-orders/${item.id}`,
        facilityId: item.facility?.id ?? undefined,
        facilityName: item.facility?.facilityName ?? undefined,
        status: item.status ?? undefined,
        meta: { workOrderNumber: item.workOrderNumber },
      }),
    );
}

export function normalizeInboundTolling(
  data: TollingAgreementEntry[],
): CalendarEvent[] {
  if (!data) return [];
  return data
    .filter((item) => item.effectiveDate != null)
    .map(
      (item): CalendarEvent => ({
        id: item.id,
        title: item.agreementNumber
          ? `Inbound Tolling ${item.agreementNumber}`
          : `Inbound Tolling ${item.id?.slice(0, 8)}`,
        start: item.effectiveDate ?? '',
        end: item.expirationDate ?? undefined,
        allDay: true,
        module: 'production',
        source: 'inboundTolling',
        kind: 'range',
        entityUrl: `/production/inbound-tolling/${item.id}`,
        facilityId: item.facility?.id ?? undefined,
        facilityName: item.facility?.facilityName ?? undefined,
        status: item.isActive ? 'active' : 'inactive',
        meta: { agreementNumber: item.agreementNumber },
      }),
    );
}

export function normalizeOutboundTolling(
  data: TollingAgreementEntry[],
): CalendarEvent[] {
  if (!data) return [];
  return data
    .filter((item) => item.effectiveDate != null)
    .map(
      (item): CalendarEvent => ({
        id: item.id,
        title: item.agreementNumber
          ? `Outbound Tolling ${item.agreementNumber}`
          : `Outbound Tolling ${item.id?.slice(0, 8)}`,
        start: item.effectiveDate ?? '',
        end: item.expirationDate ?? undefined,
        allDay: true,
        module: 'production',
        source: 'outboundTolling',
        kind: 'range',
        entityUrl: `/production/outbound-tolling/${item.id}`,
        facilityId: item.facility?.id ?? undefined,
        facilityName: item.facility?.facilityName ?? undefined,
        status: item.isActive ? 'active' : 'inactive',
        meta: { agreementNumber: item.agreementNumber },
      }),
    );
}

export function normalizeDispatchRecords(
  data: DispatchRecordEntry[],
): CalendarEvent[] {
  if (!data) return [];
  return data
    .filter((item) => item.submittedAt != null)
    .map(
      (item): CalendarEvent => ({
        id: item.id,
        title: item.externalReferenceId
          ? `Dispatch ${item.externalReferenceId}`
          : `Dispatch ${item.dispatchType || item.id?.slice(0, 8)}`,
        start: item.submittedAt ?? '',
        allDay: false,
        module: 'compliance',
        source: 'dispatchRecord',
        kind: 'point',
        entityUrl: `/compliance/dispatch-records/${item.id}`,
        facilityId: item.facilityId ?? undefined,
        facilityName: undefined,
        status: item.status ?? undefined,
        meta: {
          dispatchType: item.dispatchType,
          externalReferenceId: item.externalReferenceId,
        },
      }),
    );
}

export function normalizeInvoices(data: InvoiceEntry[]): CalendarEvent[] {
  if (!data) return [];
  return data
    .filter((item) => item.dueDate != null)
    .map(
      (item): CalendarEvent => ({
        id: item.id,
        title: item.documentNumber
          ? `Invoice ${item.documentNumber}`
          : `Invoice ${item.id?.slice(0, 8)}`,
        start: item.dueDate ?? '',
        allDay: true,
        module: 'finance',
        source: 'invoice',
        kind: 'point',
        entityUrl: `/finance/invoices/${item.id}`,
        facilityId: item.facility?.id ?? undefined,
        facilityName: undefined,
        status: item.status ?? undefined,
        meta: { documentNumber: item.documentNumber, amount: item.amount },
      }),
    );
}

export function normalizeBills(data: BillEntry[]): CalendarEvent[] {
  if (!data) return [];
  return data
    .filter((item) => item.dueDate != null)
    .map(
      (item): CalendarEvent => ({
        id: item.id,
        title: item.documentNumber
          ? `Bill ${item.documentNumber}`
          : `Bill ${item.id?.slice(0, 8)}`,
        start: item.dueDate ?? '',
        allDay: true,
        module: 'finance',
        source: 'bill',
        kind: 'point',
        entityUrl: `/finance/bills/${item.id}`,
        facilityId: item.facility?.id ?? undefined,
        facilityName: undefined,
        status: item.status ?? undefined,
        meta: { documentNumber: item.documentNumber, amount: item.amount },
      }),
    );
}

export function normalizeInventoryMovements(
  data: InventoryMovementEntry[],
): CalendarEvent[] {
  if (!data) return [];
  return data
    .filter((item) => item.occurredAt != null)
    .map((item): CalendarEvent => {
      const itemLabel = item.item?.name || item.item?.skuNumber || '';
      const typeLabel = item.movementType || '';
      return {
        id: item.id,
        title:
          [itemLabel, typeLabel].filter(Boolean).join(' - ') ||
          `Movement ${item.id?.slice(0, 8)}`,
        start: item.occurredAt ?? '',
        allDay: false,
        module: 'inventory',
        source: 'inventoryMovement',
        kind: 'point',
        entityUrl: `/inventory/movements/${item.id}`,
        facilityId: item.facilityId ?? undefined,
        facilityName: undefined,
        status: item.status ?? undefined,
        meta: {
          itemName: item.item?.name,
          skuNumber: item.item?.skuNumber,
          movementType: item.movementType,
          quantity: item.quantity,
        },
      };
    });
}
